import { useEffect, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { logError } from '@providers/ErrorTracking';
import s from './PDFRenderer.module.scss';

type Props = {
  url: string;
  setError: (value: boolean) => void;
};

const OPTIONS = {
  cMapPacked: true,
  cMapUrl: 'cmaps/',
};

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

const PDFRenderer: React.FC<Props> = ({ url, setError }) => {
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const [pagesNumber, setPagesNumber] = useState<number | null>(null);

  const onDocumentLoadSuccess: Parameters<typeof Document>[0]['onLoadSuccess'] = ({ numPages }): void => {
    setPagesNumber(numPages);
    setIsDocumentReady(true);
  };

  const onDocumentLoadError: Parameters<typeof Document>[0]['onLoadError'] = (error) => {
    logError(error);
    setError(true);
  };

  useEffect(() => {
    setIsDocumentReady(false);
  }, [url]);

  return (
    <div className={s.container}>
      <Document
        file={url}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        options={OPTIONS}
        className={s.document}
        loading={<></>}
        error={<p className={s.error}>Failed to load the document.</p>}
      >
        {isDocumentReady && pagesNumber && Array.from(new Array(pagesNumber), (_, index) => (
          <Page
            key={`${url}_${index + 1}`}
            pageNumber={index + 1}
            renderMode="canvas"
            renderAnnotationLayer={false}
            renderTextLayer={false}
            loading={<></>}
            className={s.page}
            width={1600}
          />
        ))}
      </Document>
    </div>
  );
};

// Add a wrapper with the url as the key to force a re-mount if the url changes.
const PDFRendererWrapper: React.FC<Props> = ({ url, setError }) => (
  <PDFRenderer url={url} key={url} setError={setError} />
);

export default PDFRendererWrapper;
